import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Users, ShieldCheck, SquaresFour } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CompanionPublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/rides/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ rides: [] }));
  }, []);

  useSeo({
    title: "Companion rides to work — share the run, free up the car park | Unity & Utility",
    description: "A lift and a bit of company to the office, out to a work site or a lone survey and back home. Share the run with your own colleagues, split the mileage, free up a parking bay and tap landed safe at the other end.",
    path: "/rides",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Companion rides", "/rides"]]),
  });

  const stats = [
    ["Runs posted", board?.open_rides, "rides-stat-runs"],
    ["Seats going spare", board?.seats_free, "rides-stat-seats"],
    ["Riding together", board?.sharing, "rides-stat-sharing"],
    ["Bays freed or shared", board?.bays_freed, "rides-stat-bays"],
    ["Parcels going the same way", board?.parcels_count, "rides-stat-parcels"],
    ["Delivered by a colleague", board?.delivered, "rides-stat-delivered"],
  ];

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="rides-public-page">
        <Crumbs items={[["Home", "/"], ["Companion rides", "/rides"]]} />
        <p className="overline text-[#B3241B]">Companion rides</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Car size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          Nobody travels alone
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="rides-public-intro">
          The run to the office, out to a work site, the long drive to a lone survey, and the trip
          home after a late finish — posted to your own colleagues, crew and boss rather than
          strangers. Most drivers will drop you anywhere along the way, there or back, in anything
          from a car or pickup to a crew minibus, a hire van or a pillion seat. Split the mileage,
          keep each other company, and every seat filled is one less car hunting a space at the
          works gate or on your own street.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/rides`}
            text="Share the run to work with your own crew — company on the road, mileage split, a parcel dropped on the way, and one less car on the car park."
            testid="rides-public-share" instagram />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`}
            alt="Two colleagues in hi-vis sharing a car on an early morning run to work"
            loading="lazy" className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="rides-public-image" />
        )}

        <section className="mt-6 border border-[#002FA7] bg-white p-5" data-testid="rides-public-card">
          <p className="overline text-[#002FA7]">
            Rides today{board?.day ? ` · ${board.day}` : ""}
            {board?.auto_posted && <span className="ml-2 text-[#0B5C2E]">Posted to Facebook</span>}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {stats.map(([label, value, testid]) => (
              <div key={testid} className="border border-border bg-[#FAFAF8] p-3" data-testid={testid}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">{value ?? 0}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-700" data-testid="rides-public-parking">
            <SquaresFour size={14} className="mr-1 inline align-[-2px] text-[#0B5C2E]" />
            {board?.cars_off || 0} fewer cars looking for a space today. Hand your bay over to
            whoever is driving in, or share one between you — parking sorts itself out when people
            travel together.
          </p>
        </section>

        {(board?.rides || []).length > 0 && (
          <section className="mt-8" data-testid="rides-public-list">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              Runs going out
            </h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.rides.map((r, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`rides-public-ride-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{r.leg_name}</p>
                  <p className="text-xs text-slate-500">
                    {r.from_place} → {r.to_place} · {r.when}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    {r.owner_name} · {r.vehicle} · {r.seats_left} seat(s) left
                    {r.company === "rather-not-alone" ? " · would rather not travel alone" : ""}
                    {r.drop_anywhere ? " · drops you anywhere on the way" : ""}
                    {r.return_name && r.return_name !== "One way only" ? ` · ${r.return_name.toLowerCase()}` : ""}
                    {(r.stops || []).length ? ` · via ${r.stops.join(", ")}` : ""}
                    {r.parking === "hand-over" ? " · bay going spare" : ""}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {(board?.parcels || []).length > 0 && (
          <section className="mt-8" data-testid="rides-public-parcels">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              Letters and parcels looking for a lift
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700">
              A letter, a set of signed job sheets, a box of parts or a pallet — if somebody is
              already driving past both ends, it goes with them. Picked up and dropped off with a
              tap, and a photo at the door when it matters.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.parcels.map((p, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`rides-public-parcel-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{p.what}</p>
                  <p className="text-xs text-slate-500">
                    {p.kind_name} · {p.from_place} → {p.to_place}
                    {p.by_when ? ` · by ${p.by_when}` : ""}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    {p.sender_name} · {p.status}
                    {p.free ? " · no charge" : ` · ${p.symbol}${Number(p.fee || 0).toFixed(2)} to the driver`}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {(board?.parking_bays || []).length > 0 && (
          <section className="mt-8" data-testid="rides-public-parking">
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              Somewhere to leave the vehicle
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700">
              Works bays, driveways, yards and laybys going spare — by the hour, the day or the
              month. And the more people come in together, the fewer bays anybody needs.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {board.parking_bays.map((b, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`rides-public-bay-${i}`}>
                  <p className="text-sm font-semibold text-[#0F172A]">{b.name}</p>
                  <p className="text-xs text-slate-500">
                    {b.fits_name} · {b.kind}{b.where ? ` · ${b.where}` : ""} ·
                    {" "}{b.walk_minutes} min walk{b.covered ? " · covered" : ""}
                    {b.secure ? " · secure" : ""}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {b.symbol}{Number(b.rate || 0).toFixed(2)} per {b.basis}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8" data-testid="rides-public-legs">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            The legs people share
          </h2>
          <ul className="mt-3 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
            {(board?.legs || []).map((l) => (
              <li key={l.key} className="border border-border bg-white px-4 py-3"
                data-testid={`rides-public-leg-${l.key}`}>
                <Users size={14} className="mr-2 inline align-[-2px] text-[#002FA7]" />{l.name}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10 border border-border bg-white p-5" data-testid="rides-public-safety">
          <p className="overline text-[#0B5C2E]">
            <ShieldCheck size={13} className="mr-1 inline align-[-2px]" /> Landed safe
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Every run records who is on board and when they are due back, and anyone on it can tap
            landed safe at the other end — the driver, the riders and whoever the run was shared
            with all get told. Nobody sits at home wondering.
          </p>
          <Link to="/register" className="mt-3 inline-block text-sm font-semibold text-[#002FA7] underline"
            data-testid="rides-public-join">
            Join and post your run
          </Link>
        </section>
      </div>
    </PublicShell>
  );
}
